import React,{ useState,useEffect, useMemo, useRef } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Button,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Autocomplete,
} from '@mui/material';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function DriverEarning() {

  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const tableRef = useRef(null);

  const settings = useSelector(state => state.settingsdata.settings);

  const columns =  [
      { title: t('driver_name'), field: 'driverName',
        render: rowData => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AccountBalanceWalletIcon sx={{ color: '#16A34A', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
                {rowData.driverName || '-'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                {rowData.driverVehicleNo || '-'}
              </Typography>
            </Box>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('period'), field: 'monthsName',
        render: rowData => (
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
              {rowData.monthsName || '-'}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
              {rowData.year}
            </Typography>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('booking_count'), field: 'total_rides',
        render: rowData => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ color: '#6B7280', fontSize: 16 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
              {rowData.total_rides || 0}
            </Typography>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('status'), field: 'status',
        render: rowData => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#16A34A' }} />
            <Typography sx={{ fontSize: 13, color: '#16A34A', fontWeight: 500 }}>
              {t('active') || 'Active'}
            </Typography>
          </Box>
        ),
        cellStyle: { padding: '14px 16px' },
      },
      { title: t('earning_amount'), field: 'driverShare',
        render: rowData => (
          <Chip
            label={settings.swipe_symbol ? `${rowData.driverShare}${settings.symbol}` : `${settings.symbol}${rowData.driverShare}`}
            size="small"
            sx={{
              bgcolor: '#DCFCE7',
              color: '#16A34A',
              fontWeight: 600,
              fontSize: 13,
              height: 28,
            }}
          />
        ),
        cellStyle: { padding: '14px 16px' },
      }
  ];

  const [data, setData] = useState([]);
  const driverearningdata = useSelector(state => state.driverearningdata);

  useEffect(()=>{
        if(driverearningdata.driverearnings){
            setData(driverearningdata.driverearnings);
        }
  },[driverearningdata.driverearnings]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter states
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  // Export menu
  const [exportAnchor, setExportAnchor] = useState(null);

  // Get unique drivers, vehicles, and years for filters
  const filterOptions = useMemo(() => {
    const drivers = [...new Set(data.map(d => d.driverName).filter(Boolean))];
    const vehicles = [...new Set(data.map(d => d.driverVehicleNo).filter(Boolean))];
    const years = [...new Set(data.map(d => d.year).filter(Boolean))].sort((a, b) => b - a);
    return { drivers, vehicles, years };
  }, [data]);

  // Filtered data with all filters applied
  const filteredData = useMemo(() => {
    let result = data;

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.driverName && d.driverName.toLowerCase().includes(query)) ||
        (d.driverVehicleNo && d.driverVehicleNo.toLowerCase().includes(query)) ||
        (d.monthsName && d.monthsName.toLowerCase().includes(query)) ||
        (d.year && String(d.year).includes(query))
      );
    }

    // Driver filter
    if (selectedDriver) {
      result = result.filter(d => d.driverName === selectedDriver);
    }

    // Vehicle filter
    if (selectedVehicle) {
      result = result.filter(d => d.driverVehicleNo === selectedVehicle);
    }

    // Month filter
    if (selectedMonth) {
      result = result.filter(d => d.monthsName === selectedMonth);
    }

    // Year filter
    if (selectedYear) {
      result = result.filter(d => d.year === parseInt(selectedYear));
    }

    return result;
  }, [data, searchQuery, selectedDriver, selectedVehicle, selectedMonth, selectedYear]);

  // Summary calculations based on filtered data
  const summaryData = useMemo(() => {
    const totalEarnings = filteredData.reduce((sum, d) => sum + (parseFloat(d.driverShare) || 0), 0);
    const totalRides = filteredData.reduce((sum, d) => sum + (parseInt(d.total_rides) || 0), 0);
    const uniqueDrivers = new Set(filteredData.map(d => d.driverName)).size;
    const avgEarning = filteredData.length > 0 ? totalEarnings / filteredData.length : 0;
    return { totalEarnings, totalRides, uniqueDrivers, avgEarning };
  }, [filteredData]);

  // Check if any filter is active
  const hasActiveFilters = selectedDriver || selectedVehicle || selectedMonth || selectedYear;

  // Clear all filters
  const clearFilters = () => {
    setSelectedDriver(null);
    setSelectedVehicle(null);
    setSelectedMonth('');
    setSelectedYear('');
    setSearchQuery('');
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [t('driver_name'), t('vehicle_reg_no'), t('months'), t('year'), t('booking_count'), t('earning_amount')];
    const csvData = filteredData.map(d => [
      d.driverName || '',
      d.driverVehicleNo || '',
      d.monthsName || '',
      d.year || '',
      d.total_rides || 0,
      d.driverShare || 0
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `driver_earnings_${dayjs().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setExportAnchor(null);
  };

  // Export to PDF
  const exportToPDF = () => {
    // Create a simple HTML table for PDF
    const tableHTML = `
      <html>
        <head>
          <title>${t('driver_earning') || 'Driver Earnings Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #111827; font-size: 24px; margin-bottom: 10px; }
            .subtitle { color: #6B7280; margin-bottom: 20px; }
            .summary { display: flex; gap: 20px; margin-bottom: 20px; }
            .summary-card { padding: 15px; border: 1px solid #E5E7EB; border-radius: 8px; }
            .summary-label { color: #6B7280; font-size: 12px; }
            .summary-value { font-size: 20px; font-weight: bold; color: #111827; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #F3F4F6; padding: 12px; text-align: left; font-weight: 600; border-bottom: 2px solid #E5E7EB; }
            td { padding: 12px; border-bottom: 1px solid #E5E7EB; }
            .footer { margin-top: 30px; color: #6B7280; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>${t('driver_earning') || 'Driver Earnings Report'}</h1>
          <p class="subtitle">${t('generated_on') || 'Generated on'}: ${dayjs().format('MMMM D, YYYY')}</p>
          <div class="summary">
            <div class="summary-card">
              <div class="summary-label">${t('total_earnings') || 'Total Earnings'}</div>
              <div class="summary-value">${settings.symbol}${summaryData.totalEarnings.toFixed(2)}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">${t('total_rides') || 'Total Rides'}</div>
              <div class="summary-value">${summaryData.totalRides}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">${t('drivers') || 'Drivers'}</div>
              <div class="summary-value">${summaryData.uniqueDrivers}</div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>${t('driver_name')}</th>
                <th>${t('vehicle_reg_no')}</th>
                <th>${t('period')}</th>
                <th>${t('booking_count')}</th>
                <th>${t('earning_amount')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(d => `
                <tr>
                  <td>${d.driverName || '-'}</td>
                  <td>${d.driverVehicleNo || '-'}</td>
                  <td>${d.monthsName || '-'} ${d.year || ''}</td>
                  <td>${d.total_rides || 0}</td>
                  <td>${settings.symbol}${d.driverShare || 0}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            ${t('total_records') || 'Total Records'}: ${filteredData.length}
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.print();
    setExportAnchor(null);
  };

  return (
    driverearningdata.loading? <CircularLoading/>:
    <Box style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('driver_earning') || 'Driver Earnings'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('driver_earning_description') || 'View and track driver earnings by period'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
            sx={{
              bgcolor: '#111827',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              '&:hover': { bgcolor: '#374151' },
            }}
          >
            {t('export') || 'Export'}
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 150 } }}
          >
            <MenuItem onClick={exportToCSV} sx={{ gap: 1.5 }}>
              <FileDownloadIcon sx={{ fontSize: 18, color: '#16A34A' }} />
              {t('export_csv') || 'Export CSV'}
            </MenuItem>
            <MenuItem onClick={exportToPDF} sx={{ gap: 1.5 }}>
              <PictureAsPdfIcon sx={{ fontSize: 18, color: '#DC2626' }} />
              {t('export_pdf') || 'Export PDF'}
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccountBalanceWalletIcon sx={{ color: '#16A34A', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_earnings') || 'Total Earnings'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {settings.swipe_symbol ? `${summaryData.totalEarnings.toFixed(settings.decimal || 2)}${settings.symbol}` : `${settings.symbol}${summaryData.totalEarnings.toFixed(settings.decimal || 2)}`}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptIcon sx={{ color: '#2563EB', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('total_rides') || 'Total Rides'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {summaryData.totalRides}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DirectionsCarIcon sx={{ color: '#D97706', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('drivers') || 'Drivers'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {summaryData.uniqueDrivers}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ bgcolor: '#FFFFFF', borderRadius: 3, border: '1px solid #E5E7EB', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TrendingUpIcon sx={{ color: '#9333EA', fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 13, color: '#6B7280', fontWeight: 500 }}>
                {t('avg_earning') || 'Avg Earning'}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>
              {settings.swipe_symbol ? `${summaryData.avgEarning.toFixed(settings.decimal || 2)}${settings.symbol}` : `${settings.symbol}${summaryData.avgEarning.toFixed(settings.decimal || 2)}`}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Filter Toolbar */}
      <Box
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        {/* Search */}
        <TextField
          size="small"
          placeholder={t('search') || 'Search...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F9FAFB',
            },
          }}
        />

        {/* Driver Filter */}
        <Autocomplete
          size="small"
          options={filterOptions.drivers}
          value={selectedDriver}
          onChange={(e, value) => setSelectedDriver(value)}
          sx={{ minWidth: 180 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('driver') || 'Driver'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#F9FAFB',
                },
              }}
            />
          )}
        />

        {/* Vehicle Filter */}
        <Autocomplete
          size="small"
          options={filterOptions.vehicles}
          value={selectedVehicle}
          onChange={(e, value) => setSelectedVehicle(value)}
          sx={{ minWidth: 160 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('vehicle') || 'Vehicle'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#F9FAFB',
                },
              }}
            />
          )}
        />

        {/* Year Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            <MenuItem value="">{t('year') || 'Year'}</MenuItem>
            {filterOptions.years.map(year => (
              <MenuItem key={year} value={year}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Month Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, bgcolor: '#F9FAFB' }}
          >
            <MenuItem value="">{t('month') || 'Month'}</MenuItem>
            {MONTHS.map(month => (
              <MenuItem key={month} value={month}>{month}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={clearFilters}
            sx={{
              color: '#DC2626',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': { bgcolor: '#FEE2E2' },
            }}
          >
            {t('clear') || 'Clear'}
          </Button>
        )}

        {/* Results Count */}
        <Typography sx={{ fontSize: 13, color: '#6B7280', ml: 'auto' }}>
          <strong style={{ color: '#111827' }}>{filteredData.length}</strong> {t('results') || 'results'}
        </Typography>
      </Box>

    <MaterialTable
      tableRef={tableRef}
      icons={TableIcons}
      title={null}
      columns={columns}
      style={{
        direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
        borderRadius: 12,
        boxShadow: 'none',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
      }}
      data={filteredData}
      onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
      options={{
        ...tableOptions,
        search: false,
        toolbar: false,
        showTitle: false,
        exportButton: false,
        rowStyle: rowData => ({
          backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
        }),
      }}
      detailPanel={rowData => (
        <Box sx={{ p: 3, bgcolor: '#F9FAFB' }}>
          <Grid container spacing={3}>
            {/* Driver Info */}
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                {t('driver_info') || 'Driver Info'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('driver_name') || 'Name'}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.driverName || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('vehicle_reg_no') || 'Vehicle No'}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.driverVehicleNo || '-'}</Typography>
                </Box>
              </Box>
            </Grid>
            {/* Earning Details */}
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                {t('earning_details') || 'Earning Details'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('total_rides') || 'Total Rides'}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.total_rides || 0}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('earning_amount') || 'Earnings'}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#16A34A' }}>
                    {settings.swipe_symbol ? `${rowData.driverShare}${settings.symbol}` : `${settings.symbol}${rowData.driverShare}`}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {/* Period Info */}
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
                {t('period_info') || 'Period Info'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('year') || 'Year'}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.year || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 13, color: '#6B7280' }}>{t('months') || 'Month'}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{rowData.monthsName || '-'}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      localization={{
        toolbar: {
          searchPlaceholder: (t('search')),
          exportTitle: (t('export')),
        },
        pagination: {
          labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
          firstTooltip: (t('first_page_tooltip')),
          previousTooltip: (t('previous_page_tooltip')),
          nextTooltip: (t('next_page_tooltip')),
          lastTooltip: (t('last_page_tooltip'))
        },
      }}

    />
    </Box>
  );
}
